import React, { useState, useEffect, useCallback } from 'react';
import PlaybookStatusBadge from './PlaybookStatusBadge';
import TransitionButton from './TransitionButton';
import TransitionExecutionModal from './TransitionExecutionModal';
import { playbookApi } from '../api/playbookApi';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { PLAYBOOK_KEYFRAMES } from '../types/playbook.types';
import type {
  PlaybookInstance,
  AvailableTransition,
  PlaybookStatusCategory,
  ExecuteTransitionResponse,
} from '../types/playbook.types';

interface TaskPlaybookPanelProps {
  taskId: string;
}

export const TaskPlaybookPanel: React.FC<TaskPlaybookPanelProps> = ({ taskId }) => {
  const [instance, setInstance] = useState<PlaybookInstance | null>(null);
  const [transitions, setTransitions] = useState<AvailableTransition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransition, setSelectedTransition] = useState<AvailableTransition | null>(null);
  const { isAdmin } = useUserPermissions();

  const loadInstance = useCallback(async () => {
    setLoading(true);
    try {
      // The API doesn't have a direct task->instance lookup, so we'll check if the
      // instance was loaded as part of the matter context. For now, we try to get
      // the instance via the task API pattern — the backend associates task_id with instances.
      // We'll use a search approach: list instances for the task's matter, then find matching task_id.
      // This requires the parent to pass matterId, but we keep it simple — if no instance found, render nothing.
      const res = await playbookApi.getInstance(taskId);
      const data = res as any;
      setInstance(data);

      if (data?.id) {
        const transRes = await playbookApi.getAvailableTransitions(data.id);
        const transData = transRes as any;
        setTransitions(transData.transitions || []);
      }
    } catch {
      // No playbook instance for this task — that's fine, render nothing
      setInstance(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadInstance();
  }, [loadInstance]);

  const handleTransitionComplete = useCallback(
    (_result: ExecuteTransitionResponse) => {
      setSelectedTransition(null);
      loadInstance();
    },
    [loadInstance],
  );

  if (loading) return null;
  if (!instance) return null;

  const statusCategory: PlaybookStatusCategory | 'blocked' = instance.error_state
    ? 'blocked'
    : instance.currentStatus?.category || 'todo';

  return (
    <>
      <style>{PLAYBOOK_KEYFRAMES}</style>

      <div
        className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3"
        style={{ animation: 'fadeSlideIn 300ms ease-out' }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Playbook Status
          </span>
          <PlaybookStatusBadge
            category={statusCategory}
            name={instance.currentStatus?.name || 'Unknown'}
          />
        </div>

        <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
          {instance.playbook?.name || 'Playbook'}{' '}
          <span className="text-gray-400 dark:text-gray-500">v{instance.playbook_version}</span>
        </div>

        {/* Inline transition buttons */}
        {transitions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {transitions.slice(0, 3).map((t) => (
              <TransitionButton
                key={t.transitionId}
                transition={t}
                isAdmin={isAdmin}
                onExecute={(tid) => {
                  const tr = transitions.find((x) => x.transitionId === tid);
                  if (tr) setSelectedTransition(tr);
                }}
                onForce={() => {}} // Force not available from task panel
              />
            ))}
          </div>
        )}
      </div>

      {/* Transition modal */}
      {selectedTransition && instance && (
        <TransitionExecutionModal
          isOpen={!!selectedTransition}
          instanceId={instance.id}
          transition={selectedTransition}
          onClose={() => setSelectedTransition(null)}
          onComplete={handleTransitionComplete}
        />
      )}
    </>
  );
};

export default TaskPlaybookPanel;
